import React, { useState, useEffect } from "react";
import allImgPaths from "assets/images";
import CustomCheckBox from "components/CustomCheckBox";
import Toast from "../Toast";
import MonitorIcon from "@mui/icons-material/Monitor";
import MonitorModal from "./MonitorModal";
import { getMonitoringSetting } from "services/monitoring";
import { getPatentDetails } from "services/project";
import ClaimTreeSelector from "../../ClaimTreeSelector";

interface SearchQueriesModalProps {
    isOpen: boolean;
    onClose: () => void;
    queriesData: {
        patent_id: string;
        cache_key: string;
        product_query: string;
        patent_query: string;
        prior_art_query: string;
        elements_for_profiling: string[];
    } | null;
    isLoading: boolean;
    onRegenerateQueries: (selectedElements: string[]) => void;
    onFindSimilarProducts: (queries: {
        product_query?: string;
        patent_query?: string;
        prior_art_query?: string;
        num_results: number;
        autoVijilize?: boolean;
    }) => void;
    isRegenerating: boolean;
    isFinding: boolean;
    projectId?: string;
    onClaimChange?: (claimNumber: number) => void;
    isClaimChanging?: boolean;
}

const SearchQueriesModal: React.FC<SearchQueriesModalProps> = ({
    isOpen,
    onClose,
    queriesData,
    isLoading,
    onRegenerateQueries,
    onFindSimilarProducts,
    isRegenerating,
    isFinding,
    projectId = "",
    onClaimChange,
    isClaimChanging = false,
}) => {
    const [isShowMonitor, setIsShowMonitor] = useState(false);
    const [monitoringActive, setMonitoringActive] = useState(false);

    const fetchMonitoringStatus = async () => {
        if (!projectId || !queriesData?.patent_id) return;
        try {
            const res: any = await getMonitoringSetting(projectId, queriesData.patent_id, "similar_products");
            setMonitoringActive(res?.is_enabled || false);
        } catch {
            setMonitoringActive(false);
        }
    };

    useEffect(() => {
        if (isOpen && projectId) fetchMonitoringStatus();
    }, [isOpen, projectId]);

    const [productQuery, setProductQuery] = useState("");
    const [patentQuery, setPatentQuery] = useState("");
    const [priorArtQuery, setPriorArtQuery] = useState("");
    const [allElements, setAllElements] = useState<string[]>([]);
    const [selectedElements, setSelectedElements] = useState<string[]>([]);
    // Claim selector — lets the user pick which claim's elements to profile.
    const [claimKeys, setClaimKeys] = useState<string[]>([]);
    const [selectedClaim, setSelectedClaim] = useState<string>("");
    const [claimStructure, setClaimStructure] = useState<string>("");
    const [claimsLoading, setClaimsLoading] = useState<boolean>(false);
    const [selectClaimsOpen, setSelectClaimsOpen] = useState<boolean>(true);
    const [numResults, setNumResults] = useState(5);
    const [useProductQuery, setUseProductQuery] = useState(true);
    const [usePatentQuery, setUsePatentQuery] = useState(true);
    const [usePriorArtQuery, setUsePriorArtQuery] = useState(false);
    const [autoVijilize, setAutoVijilize] = useState(false);
    const [expandedQueries, setExpandedQueries] = useState<Record<string, boolean>>({
        product: false,
        patent: false,
        priorArt: false,
    });
    const isInitialLoad = React.useRef(true);

    useEffect(() => {
        if (queriesData) {
            setProductQuery(queriesData.product_query || "");
            setPatentQuery(queriesData.patent_query || "");
            setPriorArtQuery(queriesData.prior_art_query || "");
            if (isInitialLoad.current) {
                const elements = queriesData.elements_for_profiling || [];
                setAllElements(elements);
                setSelectedElements(elements);
                isInitialLoad.current = false;
            }
        }
    }, [queriesData]);

    // Fetch the patent's claim list so the user can pick which claim to profile.
    useEffect(() => {
        if (!isOpen || !queriesData?.patent_id) return;
        setClaimsLoading(true);
        getPatentDetails(queriesData.patent_id)
            .then((res: any) => {
                const data = res?.data || res || {};
                const claims = data.claims || {};
                const structure = data.structure || "";
                const keys = Object.keys(claims).sort((a, b) => {
                    const na = parseInt(a.replace(/\D/g, "")) || 0;
                    const nb = parseInt(b.replace(/\D/g, "")) || 0;
                    return na - nb;
                });
                setClaimKeys(keys);
                setClaimStructure(structure);
                // Default to claim 1, or the first available claim if claim 1 is absent.
                setSelectedClaim((prev) => prev || (keys.includes("claim1") ? "claim1" : keys[0] || ""));
            })
            .catch(() => setClaimKeys([]))
            .finally(() => setClaimsLoading(false));
    }, [isOpen, queriesData?.patent_id]);

    const handleClaimChange = (claimKey: string) => {
        if (!claimKey || claimKey === selectedClaim) return;
        setSelectedClaim(claimKey);
        const claimNumber = parseInt(claimKey.replace(/\D/g, "")) || 0;
        // Allow the newly-fetched claim's elements to repopulate the list.
        isInitialLoad.current = true;
        onClaimChange?.(claimNumber);
    };

    const resetState = () => {
        setProductQuery("");
        setPatentQuery("");
        setPriorArtQuery("");
        setAllElements([]);
        setSelectedElements([]);
        setClaimKeys([]);
        setSelectedClaim("");
        setClaimStructure("");
        setSelectClaimsOpen(true);
        setNumResults(5);
        setAutoVijilize(false);
        setUseProductQuery(true);
        setUsePatentQuery(true);
        setUsePriorArtQuery(false);
        isInitialLoad.current = true;
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleElementToggle = (element: string, checked: boolean) => {
        if (checked) {
            setSelectedElements((prev) => [...prev, element]);
        } else {
            setSelectedElements((prev) => prev.filter((e) => e !== element));
        }
    };

    const handleSelectAllElements = () => {
        if (selectedElements.length === allElements.length) {
            setSelectedElements([]);
        } else {
            setSelectedElements([...allElements]);
        }
    };

    const handleRegenerateQueries = () => {
        if (selectedElements.length === 0) {
            Toast.warning("Warning", "Please select at least one claim element");
            return;
        }
        onRegenerateQueries(selectedElements);
    };

    const handleFindSimilarProducts = (autoVijilizeFlag = false) => {
        if (!useProductQuery && !usePatentQuery && !usePriorArtQuery) {
            Toast.warning("Warning", "Please enable at least one query type");
            return;
        }
        onFindSimilarProducts({
            ...(useProductQuery && productQuery.trim() && { product_query: productQuery }),
            ...(usePatentQuery && patentQuery.trim() && { patent_query: patentQuery }),
            ...(usePriorArtQuery && priorArtQuery.trim() && { prior_art_query: priorArtQuery }),
            num_results: numResults,
            autoVijilize: autoVijilizeFlag,
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-black opacity-30"></div>

                <div className="relative bg-white rounded-lg w-[90vw] max-w-[1920px] max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="pl-6 pr-6 pt-6">
                        <div className="flex items-center gap-x-2">
                            <img
                                src={allImgPaths.ExaminerCitationsModalIcon}
                                alt=""
                                className="w-10 h-10"
                            />
                            <div className="absolute right-6 top-6 flex items-center gap-x-2">
                                <button
                                    className={`flex items-center gap-x-1.5 border rounded-lg px-3 py-1.5 text-sm font-interMedium transition-colors ${
                                        monitoringActive
                                            ? "border-[#1751D0] text-[#1751D0] bg-[#F5F8FD] hover:bg-[#EBF0FB]"
                                            : "border-[#D0D5DD] text-[#7F7D83] hover:bg-[#F9FAFB]"
                                    }`}
                                    onClick={() => setIsShowMonitor(true)}
                                >
                                    <MonitorIcon sx={{ width: 18, height: 18, color: monitoringActive ? "#1751D0" : "#ADACB0" }} />
                                    Monitor
                                </button>
                                <button
                                    onClick={handleClose}
                                    className="hover:bg-[#f2f4f7] rounded p-1"
                                >
                                    <img
                                        src={allImgPaths.closeIcon}
                                        alt="Close"
                                        className="w-3 h-3"
                                    />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 pt-4 pb-2">
                        <h2 className="text-base font-interSemiBold text-[#0A090B]">
                            Review Search Queries
                        </h2>
                        <p className="text-sm font-interRegular text-[#4F4D55] mt-1">
                            Review and edit the generated queries before searching. You can also
                            select which claim elements to include.
                        </p>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-6 pb-4">
                        {isLoading ? (
                            <div className="flex flex-col justify-center items-center h-60">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1751D0]"></div>
                                <p className="text-sm text-[#4F4D55] mt-4">
                                    Generating search queries...
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {/* Data Used — pick the claim whose elements drive the queries */}
                                <div className="order-1 px-4 py-3 rounded-lg border border-[#E6E6E6]">
                                    <h3 className="text-sm font-interSemiBold text-[#0A090B] mb-3">
                                        Data Used
                                    </h3>

                                    {/* Select Claims — opens the Source with the claim graph */}
                                    <div className="mb-3">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectClaimsOpen}
                                                onChange={(e) => setSelectClaimsOpen(e.target.checked)}
                                                className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                                            />
                                            <span className="text-sm font-interMedium text-[#0A090B]">
                                                Select Claims
                                            </span>
                                        </label>
                                    </div>

                                    {selectClaimsOpen && (
                                        <div className="border border-[#F0F0F0] rounded-lg overflow-hidden">
                                            <div className="border-b border-[#F0F0F0] px-3 py-2 bg-[#FDFDFD]">
                                                <p className="text-[#555F6D] text-sm font-interMedium">Source</p>
                                            </div>
                                            <div className="px-3 py-3">
                                                {claimsLoading ? (
                                                    <span className="text-xs text-[#7F7D83]">Loading claims...</span>
                                                ) : claimStructure ? (
                                                    <ClaimTreeSelector
                                                        structure={claimStructure}
                                                        claimKeys={claimKeys}
                                                        selectedClaims={selectedClaim ? [selectedClaim] : []}
                                                        onToggleClaim={(claimKey) => handleClaimChange(claimKey)}
                                                    />
                                                ) : claimKeys.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {claimKeys.map((key) => {
                                                            const isSel = key === selectedClaim;
                                                            return (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    onClick={() => handleClaimChange(key)}
                                                                    className={`px-2 py-0.5 rounded-full text-xs font-interMedium border transition-colors ${isSel
                                                                        ? "bg-[#1751D0] text-white border-[#1751D0]"
                                                                        : "bg-white text-[#4F4D55] border-[#D0D5DD] hover:border-[#1751D0]"
                                                                        }`}
                                                                >
                                                                    Claim {key.replace(/\D/g, "")}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-[#7F7D83]">No claims found</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Review Search Queries */}
                                <div className="order-3 px-4 py-3 rounded-lg border border-[#E6E6E6]">
                                    <h3 className="text-sm font-interSemiBold text-[#0A090B] mb-3">
                                        Generated Queries
                                    </h3>

                                    {/* Product Query */}
                                    <div className="mb-3 border border-[#E6E6E6] rounded-lg overflow-hidden">
                                        <div
                                            className="flex items-center justify-between px-3 py-2.5 bg-[#F9FAFB] cursor-pointer select-none"
                                            onClick={() => setExpandedQueries(prev => ({ ...prev, product: !prev.product }))}
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg
                                                    className={`w-3.5 h-3.5 text-[#4F4D55] transition-transform ${expandedQueries.product ? 'rotate-90' : ''}`}
                                                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                </svg>
                                                <span className="text-sm font-interMedium text-[#2D2B32]">
                                                    Product Query
                                                </span>
                                            </div>
                                            <label className="flex items-center gap-x-1.5 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={useProductQuery}
                                                    onChange={(e) => setUseProductQuery(e.target.checked)}
                                                    className="w-3.5 h-3.5 accent-[#3062D4] cursor-pointer"
                                                />
                                                <span className="text-xs font-interRegular text-[#4F4D55]">
                                                    Include
                                                </span>
                                            </label>
                                        </div>
                                        {expandedQueries.product && (
                                            <div className="px-3 py-2.5 border-t border-[#E6E6E6]">
                                                <textarea
                                                    value={productQuery}
                                                    onChange={(e) => setProductQuery(e.target.value)}
                                                    disabled={!useProductQuery}
                                                    className={`w-full border rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] resize-none focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4] transition-opacity ${
                                                        useProductQuery
                                                            ? "border-[#E6E6E6]"
                                                            : "border-[#E6E6E6] opacity-40 bg-[#F9FAFB] cursor-not-allowed"
                                                    }`}
                                                    rows={3}
                                                    placeholder="Product search query..."
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Patent Query */}
                                    <div className="mb-3 border border-[#E6E6E6] rounded-lg overflow-hidden">
                                        <div
                                            className="flex items-center justify-between px-3 py-2.5 bg-[#F9FAFB] cursor-pointer select-none"
                                            onClick={() => setExpandedQueries(prev => ({ ...prev, patent: !prev.patent }))}
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg
                                                    className={`w-3.5 h-3.5 text-[#4F4D55] transition-transform ${expandedQueries.patent ? 'rotate-90' : ''}`}
                                                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                </svg>
                                                <span className="text-sm font-interMedium text-[#2D2B32]">
                                                    Patent Query
                                                </span>
                                            </div>
                                            <label className="flex items-center gap-x-1.5 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={usePatentQuery}
                                                    onChange={(e) => setUsePatentQuery(e.target.checked)}
                                                    className="w-3.5 h-3.5 accent-[#3062D4] cursor-pointer"
                                                />
                                                <span className="text-xs font-interRegular text-[#4F4D55]">
                                                    Include
                                                </span>
                                            </label>
                                        </div>
                                        {expandedQueries.patent && (
                                            <div className="px-3 py-2.5 border-t border-[#E6E6E6]">
                                                <textarea
                                                    value={patentQuery}
                                                    onChange={(e) => setPatentQuery(e.target.value)}
                                                    disabled={!usePatentQuery}
                                                    className={`w-full border rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] resize-none focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4] transition-opacity ${
                                                        usePatentQuery
                                                            ? "border-[#E6E6E6]"
                                                            : "border-[#E6E6E6] opacity-40 bg-[#F9FAFB] cursor-not-allowed"
                                                    }`}
                                                    rows={3}
                                                    placeholder="Patent search query..."
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Prior Art Query */}
                                    <div className="mb-3 border border-[#E6E6E6] rounded-lg overflow-hidden">
                                        <div
                                            className="flex items-center justify-between px-3 py-2.5 bg-[#F9FAFB] cursor-pointer select-none"
                                            onClick={() => setExpandedQueries(prev => ({ ...prev, priorArt: !prev.priorArt }))}
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg
                                                    className={`w-3.5 h-3.5 text-[#4F4D55] transition-transform ${expandedQueries.priorArt ? 'rotate-90' : ''}`}
                                                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                </svg>
                                                <span className="text-sm font-interMedium text-[#2D2B32]">
                                                    Prior Art Query
                                                </span>
                                            </div>
                                            <label className="flex items-center gap-x-1.5 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={usePriorArtQuery}
                                                    onChange={(e) => setUsePriorArtQuery(e.target.checked)}
                                                    className="w-3.5 h-3.5 accent-[#3062D4] cursor-pointer"
                                                />
                                                <span className="text-xs font-interRegular text-[#4F4D55]">
                                                    Include
                                                </span>
                                            </label>
                                        </div>
                                        {expandedQueries.priorArt && (
                                            <div className="px-3 py-2.5 border-t border-[#E6E6E6]">
                                                <textarea
                                                    value={priorArtQuery}
                                                    onChange={(e) => setPriorArtQuery(e.target.value)}
                                                    disabled={!usePriorArtQuery}
                                                    className={`w-full border rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] resize-none focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4] transition-opacity ${
                                                        usePriorArtQuery
                                                            ? "border-[#E6E6E6]"
                                                            : "border-[#E6E6E6] opacity-40 bg-[#F9FAFB] cursor-not-allowed"
                                                    }`}
                                                    rows={3}
                                                    placeholder="Prior art search query..."
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Number of Results */}
                                    <div>
                                        <label className="text-sm font-interMedium text-[#2D2B32] block mb-1.5">
                                            Number of Results
                                        </label>
                                        <select
                                            value={numResults}
                                            onChange={(e) => setNumResults(Number(e.target.value))}
                                            className="border border-[#E6E6E6] rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4]"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={15}>15</option>
                                            <option value={20}>20</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Claim Elements — for the selected claim */}
                                <div className="order-2 px-4 py-3 rounded-lg border border-[#E6E6E6]">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-interSemiBold text-[#0A090B]">
                                            Claim Elements
                                        </h3>
                                        <button
                                            onClick={handleSelectAllElements}
                                            className="text-xs text-[#3062D4] hover:text-blue-800 font-interMedium"
                                        >
                                            {selectedElements.length === allElements.length
                                                ? "Deselect All"
                                                : "Select All"}
                                        </button>
                                    </div>

                                    <div className="border border-[#E6E6E6] rounded-lg overflow-hidden">
                                        <div className="px-3 py-2 bg-[#F9FAFB] border-b border-[#E6E6E6]">
                                            <span className="text-xs font-interMedium text-[#4F4D55]">
                                                {selectedElements.length} of {allElements.length} selected
                                            </span>
                                        </div>
                                        <div className="max-h-[400px] overflow-y-auto">
                                            {isClaimChanging ? (
                                                <div className="px-3 py-6 flex flex-col items-center justify-center gap-2">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#1751D0]"></div>
                                                    <p className="text-xs text-[#7F7D83]">Loading claim elements...</p>
                                                </div>
                                            ) : allElements.length === 0 ? (
                                                <div className="px-3 py-6 text-center">
                                                    <p className="text-sm text-[#7F7D83]">
                                                        No claim elements found
                                                    </p>
                                                </div>
                                            ) : (
                                                allElements.map((element, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-start gap-x-2.5 px-3 py-2.5 border-b border-[#F1F1F1] last:border-b-0 hover:bg-[#F9FAFB]"
                                                    >
                                                        <div className="flex-shrink-0 mt-0.5">
                                                            <CustomCheckBox
                                                                id={`element-${index}`}
                                                                name={`element-${index}`}
                                                                label=""
                                                                checked={selectedElements.includes(element)}
                                                                onChange={(_: string, checked: boolean) =>
                                                                    handleElementToggle(element, checked)
                                                                }
                                                                customStyle="flex-shrink-0"
                                                            />
                                                        </div>
                                                        <span className="text-sm font-interRegular text-[#0A090B] leading-[1.5]">
                                                            {element}
                                                        </span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {!isLoading && (
                        <div className="px-6 py-4 bg-white border-t border-[#E6E6E6]">
                            <div className="flex justify-between gap-4">
                                <button
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 rounded-md border border-[#E6E6E6]"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRegenerateQueries}
                                    disabled={isRegenerating || selectedElements.length === 0}
                                    className={`flex-1 px-4 py-3 text-sm font-interMedium text-[#3062D4] border border-[#3062D4] rounded-md hover:bg-blue-50 ${
                                        isRegenerating || selectedElements.length === 0
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                    }`}
                                >
                                    {isRegenerating ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#3062D4]"></span>
                                            Regenerating...
                                        </span>
                                    ) : (
                                        "Regenerate Queries"
                                    )}
                                </button>
                                <button
                                    onClick={() => handleFindSimilarProducts(false)}
                                    disabled={isFinding || (!useProductQuery && !usePatentQuery && !usePriorArtQuery)}
                                    className={`flex-1 px-4 py-3 text-sm font-interMedium text-white bg-[#3062D4] hover:bg-blue-700 rounded-md ${
                                        isFinding || (!useProductQuery && !usePatentQuery && !usePriorArtQuery)
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                    }`}
                                >
                                    {isFinding && !autoVijilize ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                                            Searching...
                                        </span>
                                    ) : (
                                        "Find Similar Products"
                                    )}
                                </button>
                                <button
                                    onClick={() => { setAutoVijilize(true); handleFindSimilarProducts(true); }}
                                    disabled={isFinding || (!useProductQuery && !usePatentQuery && !usePriorArtQuery)}
                                    className={`flex-1 px-4 py-3 text-sm font-interMedium text-white bg-[#3062D4] hover:bg-blue-700 rounded-md ${
                                        isFinding || (!useProductQuery && !usePatentQuery && !usePriorArtQuery)
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                    }`}
                                >
                                    {isFinding && autoVijilize ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                                            Auto Vijilizing...
                                        </span>
                                    ) : (
                                        `Auto Vijilize (${numResults})`
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <MonitorModal
                isShow={isShowMonitor}
                setIsShow={setIsShowMonitor}
                projectId={projectId}
                level="row"
                rowId={queriesData?.patent_id}
                itemType="patent"
                checkType="similar_products"
                onSaved={fetchMonitoringStatus}
            />
        </div>
    );
};

export default SearchQueriesModal;
